import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, ElementRef, HostListener, inject, input, model, OnInit, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { CardViewType, FieldDefValidations } from '@common/enums/enums';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { VisualStateService } from '@common/services/visual-state-service';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { StoryIconListComponent } from '@src/suite/common/controls/story-icon-list/story-icon-list';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { selectListIsMobileView } from '@src/suite/common/store/breakpoints/breakpoints.selectors';
import {
    FullscreenActions,
    OpenFullscreenModalActions,
    selectCurrentVisibleSidecar,
    selectFullscreenRecordGuid,
    selectLayoutTemplate,
    SidecarEfffectActions,
    SidecarMenu,
} from '@src/suite/common/store/fullscreen';
import { selectClientTimeZone, selectEnforceRequiredFieldsOnCreate, selectFieldDefsDict, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectIsWastebin } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectViewStateCustomCardItems, selectViewStateFilterState, selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectParentStoryByGuid, selectStoryTemplates, selectSubStoriesByParentGuid } from '@src/suite/common/store/records/records.selectors';
import { toLuxonRelativeDateString } from '@src/suite/common/utils/date-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { sanitizeStoryTextContent } from '@src/suite/common/utils/story-utils';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { AppService } from '@suite/app.service';
import { getItemString, isStoryApproveField } from '@suite/common/utils/util';

import { ExternalActionsOverlayComponent } from '../../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { DatePickerComponent } from '../../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../../controls/date-time-picker/date-time-picker';
import { DeskComponent } from '../../../controls/desk/desk';
import { StTextAreaOverlayComponent } from '../../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListComponent } from '../../../controls/staff-id-list/staff-id-list';
import { StatusComponent } from '../../../controls/status/status';
import { StoryOutputTypeComponent } from '../../../controls/story-output-type/story-output-type';
import { TagsAutocompleteComponent } from '../../../controls/tags/tags-autocomplete';
import { TopicButtonComponent } from '../../../controls/topic-button/topic-button';
import { CardLeftBorderDirective } from '../../../directives/card-left-border';

@Component({
    selector: 'st-story-item',
    imports: [
        CardLeftBorderDirective,
        CdkOverlayOrigin,
        DatePickerComponent,
        DateTimePickerComponent,
        DeskComponent,
        ExternalActionsOverlayComponent,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgTemplateOutlet,
        PushPipe,
        StaffIDListComponent,
        StatusComponent,
        StoryIconListComponent,
        StoryOutputTypeComponent,
        StTextAreaOverlayComponent,
        TagsAutocompleteComponent,
        TopicButtonComponent,
    ],
    templateUrl: './story-item.html',
    styleUrl: './story-item.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-item',
    },
})
export class StoryItemComponent extends DraggableEnpsComponent implements OnInit {
    appService = inject(AppService);
    viewStateService = inject(ViewStateService);

    store = inject(Store);
    visualStateService = inject(VisualStateService);
    globalTablesService = inject(GlobalTablesService);

    elmRef = inject(ElementRef);
    highlightPipe = inject(HighlightPipe);

    viewStateFilterState$ = this.store.select(selectViewStateFilterState);
    scriptStoryTypeLabel$ = this.store.select(selectTranslatedKey(I18nKeys.PSTWebListValuePSTStoryOutputTypeGeneric));
    viewStateCustomCardItems = this.store.selectSignal(selectViewStateCustomCardItems);
    viewStateFilterState = this.store.selectSignal(selectViewStateFilterState);
    i18n = this.store.selectSignal(selectI18N)();
    searchString = this.store.selectSignal(selectViewStateSearchString);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    isMobileView = this.store.selectSignal(selectListIsMobileView);
    isWastebin = this.store.selectSignal(selectIsWastebin);
    fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid);
    enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);
    storyTemplates = this.store.selectSignal(selectStoryTemplates);
    languageSettings = this.store.selectSignal(selectI18N);

    readonly FieldDefValidations = FieldDefValidations;
    // public pstFieldNames = PSTFieldNames;
    readonly getItemString = getItemString;
    readonly PSTFieldNames = PSTFieldNames;
    readonly addSubStoryLabelKey = I18nKeys.PSTWebAddSubStoryLabel;

    CardViewTypes = CardViewType;
    searchString$ = this.store.select(selectViewStateSearchString);
    itemStringCache = new Map<string, string>();
    toolTipData: string[] = [];
    isListOpen = false;
    isAssignmentsPanelOpen = false;
    readonly storyOwnerEl = viewChild('storyOwnerEl', { read: CdkConnectedOverlay });

    readonly data = model<StorytellingStoryRecord>(null);

    // inputs
    readonly topicRecord = input<StorytellingTopicRecord>(null);
    readonly inView = signal(false);
    readonly desks = signal<string>(this.i18n.PSTWebNotAssignedOption);
    readonly owners = signal<string>(this.i18n.PSTWebNotAssignedOption);
    readonly cardViewType = input(CardViewType.LARGE);
    readonly preview = input(false);
    readonly customCardItems = input<FieldDef[]>();
    readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly inFullscreenMode = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly inSidecarOpenedMode = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly sidecarType = input<FullscreenLayoutAreas>();

    readonly isReadOnly = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly itemClicked = output<void>();
    readonly storyDeleteEmitter = output<number>();
    readonly restoreContent = output<void>();

    // computed
    readonly customCardItemsBam = computed(() => {
        if (this.customCardItems()?.length > 0 && this.preview()) {
            return this.customCardItems();
        }
        return this.viewStateCustomCardItems();
    });

    readonly storyText = computed(() => {
        if (this.data() && this.data()?.text) {
            return sanitizeStoryTextContent(this.data().text);
        }
    });

    readonly currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    readonly updatedBy = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    });

    readonly timeUpdatedByLabel = computed(() => {
        return `${this.timeUpdatedLabel()} ${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    });

    readonly addToTopicLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebAddToTopicLabel))();
    });

    readonly addToRundownLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebAddToRundownLabel))();
    });

    readonly editable = computed(() => {
        return this.data().isWriteable && !this.data()?.Path?.isWasteBinPath && !this.data()?.isDisabled;
    });

    readonly isPreview = computed(() => {
        return this.preview() || this.data()?.isDisabled;
    });

    readonly cardDisabled = computed(() => {
        return this.isReadOnly() || !this.editable() || this.disabled();
    });

    readonly displayOpenActionButtonHoverTitle = computed(() => {
        return this.globalTablesService.getDisplayOpenActionButtonHoverTitle(this.inSidecarOpenedMode())();
    });

    readonly isInSmallView = computed(() => {
        return this.cardViewType() === CardViewType.SMALL;
    });

    readonly timeUpdatedLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedAgoLabel))()} ${toLuxonRelativeDateString(this.data()?.ModTime, this.appService.updatedAgoSignal())}`;
    });

    readonly isNewTopic = computed(() => this.topicRecord()?.isNewRecord);

    readonly subStories = computed(() => {
        const storyGuid = this.data()?.Guid;
        if (!storyGuid) return [];
        if (this.isNewTopic()) {
            return (this.topicRecord().stories || []).filter(s => (s as StorytellingStoryRecord).PSTPrimaryStoryGUID === storyGuid) as StorytellingStoryRecord[];
        }
        return this.store.selectSignal(selectSubStoriesByParentGuid)()?.get(storyGuid) || [];
    });

    @HostListener('click')
    public onClick() {
        if (this.cardDisabled()) {
            return;
        }
        if (this.isInSmallView()) {
            this.itemClicked.emit();
        }
    }

    ngOnInit() {
        setTimeout(() => {
            this.inView.set(true);
        }, 0);
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    updateStory(value: any, fieldId: string) {
        if (this.cardDisabled()) return;
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            if (!value) return;
        }
        // TODO: really bad practice to update the record directly
        if (fieldId === PSTFieldNames.PSTTags) {
            this.data().Tags = value;
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(StoriesRecordActions.updateStories({ records: [this.data()] }));
    }

    readonly rundownLabel = computed(() => {
        //TODO: This one will work correctly after backend changes for story's ( ContainerTitle ) are merged
        return this.data()?.ContainerTitle || this.i18n.PSTWebNotAssignedOption;
    });

    readonly hasRundown = computed(() => {
        return !!this.data()?.ContainerTitle;
    });

    readonly hasParentTopic = computed(() => {
        return !!this.data()?.PSTStoryTopicGUID;
    });

    readonly topicOrRundownIcon = computed(() => {
        if (this.hasRundown()) {
            return 'dns';
        } else if (this.hasParentTopic()) {
            return 'topic';
        }
        return 'link_off';
    });

    readonly topicOrRundownLabel = computed(() => {
        if (this.hasRundown()) {
            return this.data()?.ContainerTitle;
        } else if (this.hasParentTopic()) {
            return this.data()?.PSTStoryTopicTitle;
        }
        return this.i18n.PSTWebNotAttachedOption;
    });

    readonly storyTypes = computed(() => {
        return this.data()?.PSTStoryOutputType;
    });

    readonly primarySubstoriesVisible = computed(() => {
        return !!this.data()?.visibilityCriteria?.[PSTFieldNames.PSTSubstoriesSequence] && !!this.data()?.[PSTFieldNames.PSTStoryTopicGUID];
    });
    readonly isSubStory = computed(() => !!this.data()?.PSTPrimaryStoryGUID);

    readonly parentStory = computed(() => {
        const parentGuid = this.data()?.PSTPrimaryStoryGUID;
        if (!parentGuid) return null;
        return this.store.selectSignal(selectParentStoryByGuid(parentGuid))();
    });

    readonly parentStoryList = computed(() => {
        const parent = this.parentStory();
        return parent ? [parent] : [];
    });

    readonly maxTagsToShow = computed(() => {
        if (this.visualStateService.isLessThanLarge()) {
            return 4;
        }
        if (this.visualStateService.isLessThanXLarge()) {
            return 6;
        }
        if (this.viewStateFilterState() && this.appService.sideBarExpanded()) {
            return 6;
        }
        if (this.viewStateFilterState() || this.appService.sideBarExpanded()) {
            return 9;
        }
        return 12;
    });

    readonly wordCount = computed(() => {
        const parsedStoryText = parseXml(this.data()?.text, 'text/html');
        if (parsedStoryText.body) {
            const bodyChildren = parsedStoryText.body.children;
            let count = 0;
            for (const child of bodyChildren) {
                if (child?.tagName.toUpperCase() !== 'DIV') {
                    count += child?.textContent
                        ?.replace(/,|;|:/g, ' ')
                        ?.replace(/\s\s+/g, ' ')
                        ?.split(/\s+/)
                        ?.map(val => val.trim())
                        ?.filter(val => !!val).length;
                }
            }
            return `${count || 0} ${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebWordsLabel))()}`;
        }
    });

    readonly approvedVisible = computed(() => isStoryApproveField(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.Approved]));

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    constructor() {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
                this.desks.set(
                    getItemString(PSTFieldNames.PSTDesk, this.itemStringCache, this.i18n, this.globalTablesService, this.data(), this.getInnerProp, this.highlightPipe, this.searchString())
                );

                this.owners.set(
                    getItemString(PSTFieldNames.PSTAssignmentOwner, this.itemStringCache, this.i18n, this.globalTablesService, this.data(), this.getInnerProp, this.highlightPipe, this.searchString())
                );
            }
        });
    }

    onTopicOpenHandler(data: StorytellingTopicRecord) {
        if (!this.inFullscreenMode()) return;

        this.store.dispatch(
            OpenFullscreenModalActions.openTopicFullscreenDialog({
                recordGuid: data?.Guid,
                recordType: data?.Type,
                isMetadata: true,
                hideNavigation: true,
            })
        );
    }

    openRelatedStory(story: StorytellingStoryRecord) {
        if (!story?.Guid) return;

        // Story card is rendered inside a sidecar panel — navigate within sidecar
        if (this.sidecarType()) {
            this.store.dispatch(
                SidecarEfffectActions.openStoryInSidecar({
                    guid: story.Guid,
                    sidecarType: this.sidecarType(),
                    sidecarMenu: SidecarMenu.STORIES,
                })
            );
            return;
        }

        // Fullscreen dialog is already open — navigate within it
        if (this.fullscreenRecordGuid() || this.isNewTopic()) {
            this.store.dispatch(
                FullscreenActions.tryOpenStory({
                    recordGuid: story.Guid,
                    recordType: story.Type,
                    standaloneStory: false,
                })
            );
            return;
        }

        // No fullscreen dialog open — open a new one
        this.store.dispatch(
            OpenFullscreenModalActions.openStoryFullscreenDialog({
                recordGuid: story.Guid,
                recordType: story.Type,
                listGuid: null,
                standaloneStory: false,
            })
        );
    }

    openSubStoriesInSidecar() {
        const isFullscreenDialogOpen = !!this.fullscreenRecordGuid();

        // Resolve sidecar type: explicit input > currently visible sidecar in fullscreen
        let sidecarType = this.sidecarType() || (isFullscreenDialogOpen ? this.store.selectSignal(selectCurrentVisibleSidecar)() : null);

        // Fullscreen has no sidecar visible (ONLY_MAIN layout) — force sidecar open by switching to BASIC layout
        if (!sidecarType && isFullscreenDialogOpen) {
            const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
            if (currentTemplate === FullscreenLayoutTemplate.ONLY_MAIN) {
                sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                this.store.dispatch(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
            }
        }

        const parentGuid = this.data()?.Guid;

        // Not in sidecar context but in fullscreen mode — navigate to the parent story first to show its sub-stories
        if (!this.sidecarType() && this.inFullscreenMode() && parentGuid) {
            if (isFullscreenDialogOpen) {
                // Fullscreen already open — navigate to parent story within it
                this.store.dispatch(
                    FullscreenActions.tryOpenStory({
                        recordGuid: parentGuid,
                        recordType: this.data()?.Type,
                        standaloneStory: false,
                    })
                );
            } else {
                // No fullscreen open — open parent story in fullscreen with sub-stories visible
                this.store.dispatch(
                    SidecarEfffectActions.openStoryFullscreenWithSubStories({
                        recordGuid: parentGuid,
                        recordType: this.data()?.Type,
                        parentGuid,
                    })
                );
                return;
            }
        }

        //  open the sub-stories list in Sidecar
        if (sidecarType) {
            this.store.dispatch(SidecarEfffectActions.openSubStoriesInSidecar({ sidecarType, parentGuid }));
        }
    }

    createSubStory(event: { templateProgramId: string; templateGuids?: string[] }) {
        this.store.dispatch(
            PlaybookApiActions.createStoryFromTemplate({ topic: this.topicRecord(), templateProgramId: event.templateProgramId, templateGuids: event.templateGuids, parentStoryGuid: this.data().Guid })
        );
    }
}
